import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Redirect } from 'react-router'

import { MainSectionDefault } from '../../components/views/includes/Wrappers'
import { tagHotjarRecording } from '../../helpers/util'
import queryString from 'query-string'
import useHome from '../../hooks/useHome'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'
import LoginTransition from '../LoginPage/components/LoginTransition'
import ActivateForm from './components/ActivateForm'
import ErrorAlert from '../../components/views/includes/ErrorAlert'
import L1NLogoWhiteStroke from '../../svg/L1N-logo-white-stroke.svg'
import Button from '../../components/Button/Button'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useAlert from '../../hooks/useAlert'

const ActivatePage = props => {
    const { history } = props
    const [initialValues, setInitialValues] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        userId: '',
        shiptos: [],
    })
    const [error, setError] = useState('')
    const [isExpiredLink, setIsExpiredLink] = useState(false)
    const [isUserActivated, setIsUserActivated] = useState(false)
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert } = useAlert()

    const {
        handleLoadUser,
        handleActivateUser,
        handleRequestNewLink,
    } = useHome()
    const { handleLoginSuccess } = useAuth()

    const queryValues = queryString.parse(props.location.search)
    const userId = queryValues?.user

    const onRequestNewLinkSuccess = () => {
        changeLoading(false)
        history?.push(AppRoutes.login())
        handleChangeAlert({
            title: 'New Link sent success',
            message: 'Your new activation link has been sent.',
            type: 'success',
        })
    }

    const onRequestNewLinkError = () => {
        changeLoading(false)
    }

    const requestActivationLink = () => {
        changeLoading(true)
        handleRequestNewLink(
            userId,
            onRequestNewLinkSuccess,
            onRequestNewLinkError,
        )
    }

    const selectOptions = initialValues?.shiptos

    const onUserLoaded = response => {
        let user = response?.data?.user
        setInitialValues({
            email: user?.email,
            firstName: user?.first_name,
            lastName: user?.last_name,
            userId: queryValues.user,
            shiptos: user?.shiptos,
        })
        changeLoading(false)
    }

    const onLoadedUserError = err => {
        tagHotjarRecording('API Error')
        const message = err?.response?.data?.message || ''
        changeLoading(false)
        switch (message) {
            case 'User Already Verified':
                setIsUserActivated(true)
                setError(message)
                break
            case 'Link Expired':
                //Here will activate the isExpiredLink flag to activate the page
                setIsExpiredLink(true)
                setError(message)
                break
            default:
                setError(
                    'This activation link is no longer valid. Please contact support at help@liftonenow.com',
                )
                break
        }
    }

    const getUserInfo = () => {
        changeLoading(true)
        handleLoadUser(queryValues?.user, onUserLoaded, onLoadedUserError)
        tagHotjarRecording('Activation')
    }

    useEffect(() => {
        if (initialValues?.userId == '') {
            getUserInfo()
        }
    }, [])

    const onUserActivate = response => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'activationFormSubmission' })
        //login and redirect
        //put into local storage - TODO MOVE THIS OUT TO USER ACTIONS
        changeLoading(false)

        var session = JSON.stringify(response.data.session)
        localStorage.setItem('session', session)
        handleLoginSuccess(response?.data?.session)
    }

    const onUserActivateError = err => {
        let errs = err.response.data.errors
        let message = err.response.data.message

        if (errs) {
            let allErrors = Object.keys(errs)
                .map(function(elem, i) {
                    let key = Object.keys(errs)[i]
                    return errs[key][0] + ' \n'
                })
                .join(' ')

            let listErr = allErrors.split('\n').map((item, key) => {
                return (
                    <span key={key}>
                        {item}
                        <br />
                    </span>
                )
            })
            setError(listErr)
        } else {
            setError(message)
        }
        changeLoading(false)
        window.scrollTo(0, 100)
    }

    const processSubmit = userInfo => {
        setError('')
        changeLoading(true)
        handleActivateUser(
            userInfo,
            initialValues?.userId,
            initialValues?.email,
            onUserActivate,
            onUserActivateError,
        )
    }

    const renderHeader = () => {
        return (
            <section className="flex flex-col items-center justify-start w-full gap-4">
                <img className="h-8 w-28" src={L1NLogoWhiteStroke} />
                <h1 className="sm:text-3xl text-2.5xl font-bold text-white text-center">
                    Account Activation
                </h1>
            </section>
        )
    }

    const renderExpiredLink = () => {
        return (
            <MainSectionDefault>
                <div className="w-full h-auto sm:p-12 p-6 sm:gap-y-16 gap-y-10 flex flex-col items-center justify-start transition-all">
                    {renderHeader()}

                    <div className="w-full flex flex-col gap-2 items-center justify-center">
                        <p className="text-white font-medium text-xl text-center">
                            Your link has expired
                        </p>
                        <p className="text-black30 text-base font-medium text-center">
                            Please request a new activation link below and you
                            will receive a new link within 1 to 2 business days
                        </p>
                    </div>

                    <Button
                        text="Request new Activation Link"
                        className="w-full"
                        variant="secondary"
                        onClick={() => {
                            requestActivationLink()
                        }}
                    />
                </div>
            </MainSectionDefault>
        )
    }

    const renderActivatedUser = () => {
        return (
            <MainSectionDefault>
                <div className="w-full h-auto sm:p-12 p-6 sm:gap-y-16 gap-y-10 flex flex-col items-center justify-start transition-all">
                    {renderHeader()}

                    <div className="w-full flex flex-col gap-2 items-center justify-center">
                        <p className="text-white font-medium text-xl text-center">
                            User already activated
                        </p>
                        <p className="text-black30 text-base font-medium text-center">
                            Your email is already activated, please{' '}
                            <Link
                                to={AppRoutes.login()}
                                className="text-yellow underline"
                            >
                                log in
                            </Link>{' '}
                            to view your information
                        </p>
                    </div>
                </div>
            </MainSectionDefault>
        )
    }
    return undefined === queryValues.signature ? (
        <Redirect to={AppRoutes.login()} />
    ) : isExpiredLink ? (
        renderExpiredLink()
    ) : isUserActivated ? (
        renderActivatedUser()
    ) : (
        <LoginTransition>
            <div className="w-full h-auto sm:p-12 p-6 sm:gap-y-6 gap-y-10 flex flex-col items-center justify-start transition-all">
                {error && <ErrorAlert error={error} />}

                {renderHeader()}

                <span className="font-medium text-sm text-white flex items-center justify-center">
                    Already have an account?{' '}
                    <Link
                        to={AppRoutes.login()}
                        className="text-yellow underline ml-1"
                    >
                        Log in
                    </Link>
                </span>

                <ActivateForm
                    initialValues={initialValues}
                    onSubmit={processSubmit}
                    locations={selectOptions}
                />

                <span className="font-normal text-sm text-white flex sm:flex-row flex-wrap flex-col items-center justify-center">
                    By submitting this form you agree to our{' '}
                    <Link
                        to={AppRoutes.termsAndConditions()}
                        className="text-yellow underline ml-1"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Terms and Conditions
                    </Link>
                </span>
            </div>
        </LoginTransition>
    )
}

ActivatePage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string.isRequired,
    }),
    history: PropTypes.shape({
        push: PropTypes.func,
    }),
}

export default ActivatePage
